/*
 * The pharmacy's side of the system: a sidebar with its own four pages, and any home opened from
 * them in the space beside it.
 *
 * The sidebar stays put when a home is open, so getting back to the list, the deliveries or the
 * calendar is always one click rather than a trail of Back buttons. On a narrow screen it folds
 * into a strip across the top.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api } from './api.ts';
import type { Home, User } from './api.ts';
import { Notices, TopBar } from './chrome.tsx';
import { HomeView } from './home-view.tsx';
import type { HomeTab } from './home-view.tsx';
import { CareHomesPage, OverviewPage } from './pharmacy.tsx';
import { CalendarPage } from './pharmacy-calendar.tsx';
import { AllDeliveriesPage } from './pharmacy-deliveries.tsx';
import { hashFor, parseHash, sectionOf } from './pharmacy-routes.ts';
import type { Route } from './pharmacy-routes.ts';

export function PharmacyShell({ user, onSignOut }: { user: User; onSignOut: () => void }) {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));
  const [homes, setHomes] = useState<Home[] | null>(null);
  const [error, setError] = useState('');

  // The Back button, and anyone typing an address in.
  useEffect(() => {
    const follow = () => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', follow);
    return () => window.removeEventListener('hashchange', follow);
  }, []);

  const go = useCallback((next: Route) => {
    const hash = hashFor(next);
    if (window.location.hash !== hash) window.location.hash = hash;
    setRoute(next);
    window.scrollTo({ top: 0 });
  }, []);

  const loadHomes = useCallback(async () => {
    try {
      const data = await api.get<{ homes: Home[] }>('/api/homes');
      setHomes(data.homes);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the homes.');
    }
  }, []);

  useEffect(() => {
    void loadHomes();
  }, [loadHomes]);

  // Moving between a home's tabs replaces the address rather than adding to history: Back should
  // leave the home, not step back through every tab that was looked at.
  const onTabChange = useCallback((homeId: string, tab: HomeTab) => {
    const hash = hashFor({ page: 'home', homeId, tab, deliveryId: null });
    window.history.replaceState(null, '', hash);
    setRoute({ page: 'home', homeId, tab, deliveryId: null });
  }, []);

  const section = sectionOf(route);
  const openHome = route.page === 'home' ? homes?.find((h) => h.id === route.homeId) ?? null : null;

  return (
    <div className="shell">
      <TopBar user={user} onSignOut={onSignOut} />
      <div className="pharmacy">
        <aside className="sidebar">
        <nav className="side-nav" aria-label="Pharmacy">
          <SideLink icon={<IconHome />} label="Home" on={section === 'overview'} onClick={() => go({ page: 'overview' })} />
          <SideLink
            icon={<IconHomes />}
            label="Care homes"
            on={section === 'homes'}
            onClick={() => go({ page: 'homes', adding: false })}
          />
          {homes && homes.length ? (
            <ul className="side-homes">
              {homes.map((home) => (
                <li key={home.id}>
                  <button
                    type="button"
                    className={route.page === 'home' && route.homeId === home.id ? 'on' : ''}
                    onClick={() => go({ page: 'home', homeId: home.id, tab: null, deliveryId: null })}
                  >
                    {home.name}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <SideLink
            icon={<IconTruck />}
            label="Deliveries"
            on={section === 'deliveries'}
            onClick={() => go({ page: 'deliveries', show: 'waiting' })}
          />
          <SideLink
            icon={<IconCalendar />}
            label="Calendar"
            on={section === 'calendar'}
            onClick={() => go({ page: 'calendar', month: null })}
          />
        </nav>
        </aside>

        <main className="pharmacy-main">
          <Notices user={user} />
          {error ? <p className="error" role="alert">{error}</p> : null}

          {route.page === 'overview' ? (
            <OverviewPage go={go} />
          ) : route.page === 'homes' ? (
            <CareHomesPage
              go={go}
              adding={route.adding}
              onAdded={async (homeId) => {
                await loadHomes();
                go({ page: 'home', homeId, tab: null, deliveryId: null });
              }}
            />
          ) : route.page === 'deliveries' ? (
            <AllDeliveriesPage go={go} show={route.show} />
          ) : route.page === 'calendar' ? (
            <CalendarPage go={go} month={route.month} />
          ) : openHome ? (
            <>
              <p className="crumbs">
                <button type="button" className="link" onClick={() => go({ page: 'homes', adding: false })}>
                  Care homes
                </button>
                <span aria-hidden="true"> / </span>
                <span>{openHome.name}</span>
              </p>
              <HomeView
                key={openHome.id}
                user={user}
                home={openHome}
                initialTab={route.tab}
                initialDeliveryId={route.deliveryId}
                onTabChange={(tab) => onTabChange(openHome.id, tab)}
              />
            </>
          ) : homes === null ? (
            <p className="muted">Loading…</p>
          ) : (
            <section>
              <h1>That home is not here</h1>
              <p className="muted">It may have been closed, or the link may be for a home this login cannot see.</p>
              <button type="button" onClick={() => go({ page: 'homes', adding: false })}>See all care homes</button>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}

function SideLink({ icon, label, on, onClick }: { icon: ReactNode; label: string; on: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`side-link${on ? ' on' : ''}`} aria-current={on ? 'page' : undefined} onClick={onClick}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

/* Plain line icons, drawn in the text colour so they follow the theme. */
const icon = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };

function IconHome() {
  return (
    <svg {...icon}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
      <path d="M10 21v-6h4v6" />
    </svg>
  );
}

function IconHomes() {
  return (
    <svg {...icon}>
      <path d="M3 21V8l6-4 6 4v13" />
      <path d="M15 21V11l6 3v7" />
      <path d="M7 11h4M7 15h4" />
      <path d="M2 21h20" />
    </svg>
  );
}

function IconTruck() {
  return (
    <svg {...icon}>
      <path d="M2 6h11v10H2z" />
      <path d="M13 9h4l4 4v3h-8" />
      <circle cx="6" cy="18" r="2" />
      <circle cx="17" cy="18" r="2" />
    </svg>
  );
}

function IconCalendar() {
  return (
    <svg {...icon}>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}
